import { Alert, Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Network from 'expo-network';

import { EnvVars } from '../constants/EnvConfig';
import { FILTER_ALLOW_ALL } from './reducers/pantry';
import { wrapper } from '../utils/asyncFunctions';
import getInstallationIdAsync from '../utils/getInstallationIdAsync';
import { log, logError } from '../utils/logger';
import { clearLocalToken } from '../utils/token';

const { apiUrl, lighthouseApiUrl } = EnvVars;
const LIGHTHOUSE_TIMEOUT_MS = 8000;

export default {
  establishSession,
  selectLanguage,
  checkEmailOrPhone,
  enterEmail,
  enterPhone,
  doNotAskAgain,
  emailVerified,
  unsubscribeEmail,
  createPassword,
  enterPassword,
  sendMagicLink,
  loginByMagicLink,
  resetPassword,
  createUser,
  getPantries,
  getPantryByOrganizationId,
  getLighthousePantries,
  getLighthousePantryById,
  getCurrentUser,
  updateUser,
  deleteAccount,
  pushToken,
  otpConfirm,
  otpSend,
  mergeUsers,
  getTerms,
  acceptTerms,
  updateReservationsAvailableQueue,
  getVisits,
  getNextOptions,
  makeAppointment,
  cancelVisit,
  changeAppointment,
  sendFeedback,
  getUpdates,
  getThread,
  getTefapConfig,
  post,
  get,
  checkSession,
  zipcodeToState,
};

async function checkSession() {
  const expiresAt = await AsyncStorage.getItem('expiresAt');
  const sessionId = await AsyncStorage.getItem('sessionId');
  if (!expiresAt || !sessionId || parseInt(expiresAt, 10) <= Math.round(new Date().getTime() / 1000)) {
    const token = await AsyncStorage.getItem('token');
    if (token) return establishSession(token);
    return Promise.resolve();
  } else {
    return AsyncStorage.getItem('sessionId');
  }
}

/**
 * Method to conduct POST requests
 * @param {string}  url          The url path we send the request to
 * @param {object}  params       The body parameters
 * @param {object}  headers      The header parameters
 * @param {string}  method       Method, post or patch
 * @param {boolean} needsSession Does this request needs a session? Default true
 */
async function post(url, params, headers = {}, method = 'POST', needsSession = true) {
  if (needsSession) {
    const [error, sessionId] = await wrapper(checkSession());
    if (error) return Promise.reject(error);
    if (!sessionId) return Promise.resolve();
    // TODO # 3230: Implement better, safer session handling
    headers['X-UIDH'] = `SESSIONID=${sessionId}`;
  }

  // only proceed if we can reach the internet
  const NetworkState = await Network.getNetworkStateAsync();
  if (!NetworkState.isInternetReachable) {
    Alert.alert('You are not connected to the internet. Please connect and try again.');
    return Promise.resolve();
  }

  const headerParams = Object.assign(
    {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    headers
  );
  const requestParams = {
    method,
    headers: headerParams,
    body: JSON.stringify(params),
  };

  const [fError, response] = await wrapper(fetch(apiUrl + url, requestParams));
  if (fError) return Promise.reject(fError);
  if (response) {
    return response.json().then((body) => {
      return Promise.resolve({ ...body, status: response.status });
    });
  }
  return Promise.resolve();
}

/**
 * Method to conduct GET requests
 * @param {string}  url          The url path we send the request to
 * @param {object}  headers      The header parameters
 * @param {boolean} needsSession Does this request needs a session? Default true
 * @param {number}  timeout      Time in ms to wait for fetch until throwing a timed-out error (optional)
 */
async function get(url, headers = {}, needsSession = true, timeout = undefined) {
  if (needsSession) {
    const [error, sessionId] = await wrapper(checkSession());
    if (error) return Promise.reject(error);
    if (!sessionId) return Promise.resolve();
    // TODO # 3230: Implement better, safer session handling
    headers['X-UIDH'] = `SESSIONID=${sessionId}`;
  }

  // only proceed if we can reach the internet
  const NetworkState = await Network.getNetworkStateAsync();
  if (!NetworkState.isInternetReachable) {
    Alert.alert('You are not connected to the internet. Please connect and try again.');
    return Promise.resolve();
  }

  const headerParams = Object.assign(
    {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    headers
  );
  const requestParams = {
    method: 'GET',
    headers: headerParams,
  };

  const fullUrl = apiUrl + url;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  return fetch(fullUrl, {
    ...requestParams,
    signal: timeout ? controller.signal : undefined,
  })
    .then((response) => {
      log('Api.get() FULFILLED', { statusCode: response.status, url: fullUrl });
      if (response && response.ok) return Promise.resolve(response.json());
    })
    .catch(
      /**
       * @param {Error} error
       */
      (error) => {
        if (error.name === 'AbortError') log('Api.get() ABORT', { timeout, url: fullUrl });
        else log('Api.get() ERROR', { error, url: fullUrl });
        return Promise.reject(error);
      }
    )
    .finally(() => {
      clearTimeout(timeoutId);
    });
}

/**
 * Establishes a new session with the server using the provided token.
 *
 * @async
 * @function establishSession
 * @param {string} token - The authentication token to establish the session.
 * @returns {Promise<string>} A promise that resolves with the sessionId if successful.
 * @throws {Error} If the session establishment fails or the response is invalid.
 *
 * @description
 * This function sends a POST request to the server to establish a new session.
 * It handles various scenarios:
 * - If the server returns a 400 status, it clears the local token and rejects the promise.
 * - If the response contains a valid sessionId and expiresAt, it stores these in AsyncStorage.
 * - If the response is invalid or missing required fields, it rejects the promise.
 *
 * @example
 * try {
 *   const sessionId = await establishSession(userToken);
 *   console.log('Session established:', sessionId);
 * } catch (error) {
 *   console.error('Failed to establish session:', error);
 * }
 */
async function establishSession(token) {
  log('Api:establishSession');
  const [error, res] = await wrapper(post('sessions/establish', { token }, {}, 'POST', false));

  if (res?.status >= 400 && res?.status < 500 && res?.status !== 408) {
    clearLocalToken();
    throw new Error('Token no longer valid');
  }
  if (error) {
    console.error('Error establishing session:', error);
    throw error;
  }
  if (res?.sessionId && res?.expiresAt) {
    await AsyncStorage.setItem('sessionId', res.sessionId);
    await AsyncStorage.setItem('expiresAt', res.expiresAt + '');
    return res.sessionId;
  }

  throw new Error('Invalid response');
}

function selectLanguage(index) {
  return AsyncStorage.setItem('language', index.toString());
}

// no session
async function checkEmailOrPhone(searchString, language) {
  log('Api:checkEmailOrPhone');
  const deviceOS = Platform.OS;
  const deviceId = await getInstallationIdAsync();
  const params = { searchString, language, deviceOS, deviceId };
  return post('check/email_or_phone', params, {}, 'POST', false);
}

function otpSend(phone, deviceId, language) {
  const params = { phone, device_id: deviceId, language };
  return post('otp/send', params, {}, 'POST', false);
}

function otpConfirm(phone, code, deviceId, language, needsSession = false) {
  const params = { phone, code, language, device_id: deviceId, os: Platform.OS };
  return post('otp/verify', params, {}, 'POST', needsSession);
}

function emailVerified(params) {
  return post('users/email_verified', params, {}, 'POST', false);
}

function unsubscribeEmail(token, subscribed) {
  return post('user/unsubscribe/update', { token, subscribed }, {}, 'POST', false);
}

function createPassword(email, password, token) {
  // token is only used for reset password
  return post('users/create_password', { email, password, token }, {}, 'POST', false);
}

async function enterPassword(email, password, language) {
  const deviceId = await getInstallationIdAsync();
  const deviceOS = Platform.OS;
  const params = { email, password, language, deviceId, deviceOS };
  return post('users/login_password', params, {}, 'POST', false);
}

async function loginByMagicLink(token) {
  const deviceId = await getInstallationIdAsync();
  const deviceOS = Platform.OS;
  const params = { token, deviceId, deviceOS };
  return post('users/login_magic_link', params, {}, 'POST', false);
}

async function sendMagicLink(emailOrPhone, magicLinkMethod, language) {
  const params = { emailOrPhone, magicLinkMethod, language };
  return post('users/send_magic_link', params, {}, 'POST', false);
}

function resetPassword(email) {
  return post('users/reset_password', { email }, {}, 'POST', false);
}

function createUser(user) {
  log('Api:createUser');
  user.food_pick_up = false;
  return post('users/create', user, {}, 'POST', false);
}

// Email
function enterEmail(email, doSend) {
  const params = { email, do_send: doSend };
  return post('users/enter_email', params);
}

function enterPhone(phone) {
  return post('users/enter_phone', { phone });
}

function doNotAskAgain(type) {
  return post('users/do_not_ask_again', { type });
}

// Pantries
function getPantries(isLoggedIn, lat1, lng1, lat2, lng2, filter = FILTER_ALLOW_ALL, options = {}) {
  const { algoliaQuery, q, currentPosition, limit, offset } = options;

  log('Api.getPantries()', {
    boundingRectangle: { lat1, lng1, lat2, lng2 },
    algoliaQuery,
    currentPosition,
    limit,
    offset,
  });

  // Start with filter parameters
  const searchParams = Object.keys(filter).reduce((acc, filterKey) => {
    const specifiedFilters = filter[filterKey];

    // an empty array means "allow all", so use the default filter if specifiedFilters is empty
    const requestFilters = specifiedFilters.length > 0 ? specifiedFilters : FILTER_ALLOW_ALL[filterKey];
    // filter arrives as Record<string, string[]>, so join the values into querystring param
    acc[filterKey] = requestFilters.join(',');
    return acc;
  }, {});

  // Add bounding box only if provided (not null/undefined)
  // When algoliaQuery is present, bounding box should be omitted (mutually exclusive)
  if (lat1 != null && lng1 != null && lat2 != null && lng2 != null) {
    searchParams.lat1 = lat1;
    searchParams.lng1 = lng1;
    searchParams.lat2 = lat2;
    searchParams.lng2 = lng2;
  }

  // NEW: Add search query parameter (backend uses 'q' parameter, not 'algoliaQuery')
  if (algoliaQuery) {
    searchParams.q = algoliaQuery;
  } else if (q) {
    searchParams.q = q;
  }

  // NEW: Add currentPosition for distance-based sorting
  if (currentPosition) {
    searchParams.currentPosition = currentPosition;
  }

  // NEW: Add pagination parameters
  if (limit !== undefined) {
    searchParams.limit = limit;
  }
  if (offset !== undefined) {
    searchParams.offset = offset;
  }

  const url = `map/locations?${new URLSearchParams(searchParams)}`;
  return get(url, {}, isLoggedIn).then((response) => {
    if (response && response.length) return Promise.resolve(response);
    return Promise.resolve([]);
  });
}

function getPantryByOrganizationId(organizationId, isLoggedIn) {
  return get(`map/location/${organizationId}`, {}, isLoggedIn);
}

// Lighthouse (PPR) PTF endpoints — public, unauthenticated, no session.
// Contract: docs/api/ptf-locations-contract.md (in for-the-greater-good/ppr-prod).
// Soft dependency: any failure returns the fallback so callers can carry on with
// Plentiful-only data.
async function getLighthousePantries(lat1, lng1, lat2, lng2, options = {}) {
  const { q, limit, offset } = options;
  const searchParams = {};
  if (lat1 != null && lng1 != null && lat2 != null && lng2 != null) {
    searchParams.lat1 = lat1;
    searchParams.lng1 = lng1;
    searchParams.lat2 = lat2;
    searchParams.lng2 = lng2;
  }
  if (q) searchParams.q = q;
  if (limit !== undefined) searchParams.limit = limit;
  if (offset !== undefined) searchParams.offset = offset;

  const qs = new URLSearchParams(searchParams).toString();
  const url = `${lighthouseApiUrl}/api/v1/partners/ptf/locations${qs ? `?${qs}` : ''}`;
  log('Api.getLighthousePantries()', { url });
  try {
    const rows = await lighthouseFetch(url);
    if (!Array.isArray(rows)) return [];
    return rows;
  } catch (error) {
    logError(error, { context: 'getLighthousePantries', url });
    return [];
  }
}

async function getLighthousePantryById(uuid) {
  const url = `${lighthouseApiUrl}/api/v1/partners/ptf/locations/${encodeURIComponent(uuid)}`;
  log('Api.getLighthousePantryById()', { url });
  try {
    return await lighthouseFetch(url);
  } catch (error) {
    logError(error, { context: 'getLighthousePantryById', url });
    return null;
  }
}

async function lighthouseFetch(url) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), LIGHTHOUSE_TIMEOUT_MS);
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    });
    if (!response.ok) {
      throw new Error(`Lighthouse responded ${response.status}`);
    }
    return await response.json();
  } finally {
    clearTimeout(timeoutId);
  }
}

// User
function getCurrentUser() {
  return get(`users/current`);
}

function updateUser(updates) {
  return post('users/current', updates, {}, 'PATCH');
}

function pushToken(token) {
  return post('users/current/push_token_expo', { token }, {}, 'PATCH');
}

function mergeUsers(email, phone) {
  return post('users/merge', { email, phone });
}

function deleteAccount() {
  return post('users/account', {}, {}, 'DELETE');
}

// Eula
function getTerms() {
  return get('users/get_eula');
}

function acceptTerms() {
  return post('users/accept_eula', {});
}

function updateReservationsAvailableQueue(pantryId, params) {
  const url = `pantries/${pantryId}/reservations_available_notifications`;
  return post(url, params, {}, 'PATCH');
}

function getUpdates() {
  return get('update_threads');
}

function getThread(id) {
  return get(`update_threads/${id}/updates`);
}

// VISITS
function getVisits() {
  return get('visits').then((response) => {
    if (response && response.visit_list) return Promise.resolve(response.visit_list);
    return Promise.resolve([]);
  });
}

function getNextOptions(visitId) {
  return get(`next_visits/${visitId}`).then((response) => {
    if (response.status == 'success') {
      return Promise.resolve(response.results);
    }
  });
}

function makeAppointment(pantryId, serviceDate, time) {
  return post('visits/create', {
    pantry_id: pantryId,
    date: serviceDate,
    time,
  }).then((response) => {
    if (response && response.errors) return Promise.reject(response.errors);
    return Promise.resolve(response.visit);
  });
}

function cancelVisit(visitId) {
  return post(`visits/${visitId}`, {}, {}, 'DELETE');
}

function changeAppointment(visitId, serviceDate, time) {
  return post(
    `visits/${visitId}/date`,
    {
      date: serviceDate,
      time,
    },
    {},
    'PATCH'
  ).then((response) => {
    if (response && response.errors) return Promise.reject(response.errors);
    return Promise.resolve(response.visit);
  });
}

// FEEDBACK
function sendFeedback(feedback) {
  return post('feedback/submit', { feedback });
}

// TEFAP
function getTefapConfig() {
  log('Api:tefapConfig');
  return get('tefap/config');
}

// ZIPCODE

/**
 * Get the state of a given US zipcode
 * @param zipcode
 * @returns {Promise<string | null> state as two-character code, null if none found
 */
async function zipcodeToState(zipcode) {
  let response;
  try {
    response = await get(`zipcode_to_state/${zipcode}`, {}, true, 5000);
  } catch (error) {
    logError(new Error('[Api.zipcodeToState()] Error during fetch', { cause: error }));
    return null;
  }
  return response?.state ?? null;
}
